import axios, { AxiosResponse } from 'axios';

import { APIEnquiry, ManualAPIEnquiry } from '../enquiryApi';
import { getProviderId } from '../permissionService';

import { Command, IdWrapper } from '../../model/command';
import { Enquiry, Enquiries } from '../../model/enquiry';
import { Product } from '../../model/product';
import { ManualEnquiry, ManualEnquiries } from '../../model/ManualEnquiry';
import { RetryAggregateCommand } from '../../model/RetryAggregateCommand';
import TransactionState from "../../model/TransactionState";
import {EnquirySummary} from "../../model/EnquirySummary";
import { ProviderMappingResponse } from '../../model/ProviderMappingResponse';

export interface GetEnquiryParams {
  page: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: string;
  filterField?: string;
  filterValue?: string;
  excludeFilter?: boolean,
}

const format = (enq: APIEnquiry | ManualAPIEnquiry): Enquiry | ManualEnquiry => {
  const products = enq.products.reduce((result, product) => {
    const existing = result.find((prod) => prod.name === product.name);
    if (existing) {
      existing.overrides.push(product.overrides);
      return result;
    }
    return result.concat({
      name: product.name,
      overrides: [product.overrides],
    });
  }, [] as Product[]);
  return {
    ...enq,
    products,
  };
};

/* eslint-disable no-param-reassign */
const getEnquiryDetails = (url: string, token: string, requestParams: GetEnquiryParams): Promise<AxiosResponse<Enquiries>> => {
  const providerId: string = getProviderId(token);

  return axios.get<Enquiries>(url, {
    headers: {
      'X-ProviderId': providerId, // only for local testing, real users are extracted from token
      Authorization: `Bearer ${token}`,
    },
    params: {
      limit: requestParams.pageSize,
      offset: requestParams.pageSize * (requestParams.page - 1),
      sortBy: requestParams.sortOrder ? requestParams.sortField : null,
      sortDirection: requestParams.sortOrder,
      filterField: requestParams.filterField || null,
      filterValue: requestParams.filterValue || null,
      excludeFilter: requestParams.excludeFilter || null,
    },
    transformResponse: (rawData : string) => {
      const data = JSON.parse(rawData);
      return { ...data, data: data.data.map(format) };
    },
  }).then((res: AxiosResponse<Enquiries>) => {
    res.data.data.forEach((data) => {
      data.firstRequestedDate = new Date(data.firstRequestedDate);
      data.enquiryId = data.enquiryId || '';
      data.applicationId = data.applicationId || '';
      data.firstName = data.firstName || '';
      data.middleName = data.middleName || '';
      data.surname = data.surname || '';
      data.evidenceProviders.forEach((provider) => {
        provider.bookingStatusUpdated = new Date(provider.bookingStatusUpdated);
        provider.evidenceRequested = provider.evidenceRequested || '';
        provider.evidenceProvider = provider.evidenceProvider || '';
        provider.bookingStatus = provider.bookingStatus || '';
      });
      data.actioned = false;
    });
    return res;
  });
};

export const getOverrides = (
  token: string,
  requestParams: GetEnquiryParams,
): Promise<AxiosResponse<Enquiries>> => getEnquiryDetails('/sg/v2/overrides', token, requestParams);

export const getEnquiries = (
  token: string,
  requestParams: GetEnquiryParams,
): Promise<AxiosResponse<Enquiries>> => getEnquiryDetails(
  '/sg/v2/enquiries',
  token,
  {
    ...requestParams,
    // Apply default filters for the status and testing tables if nothing is supplied by the user
    filterField: requestParams.filterField ? requestParams.filterField : 'bookingStatus',
    filterValue: requestParams.filterValue ? requestParams.filterValue : 'INSUFFICIENT_DETAILS',
    excludeFilter: !(requestParams.filterField || requestParams.filterValue),
  },
);

export const overrideEnquiry = (
  enquiryId: string,
  providerId: string,
  products: Product[],
  token: string,
): Promise<AxiosResponse<string>> => {
  const command: Command = {
    enquiryId: {
      id: enquiryId,
    },
    providerId: {
      id: providerId,
    },
    decisionDetails: {},
  };

  products.forEach((product) => {
    product.overrides.forEach((override) => {
      const key = `${override.name}_${product.name}`;
      command.decisionDetails[key] = override.value;
    });
  });

  return axios.post(
    `/oc/v1/override/${enquiryId}`,
    command,
    {
      headers: {
        'X-ProviderId': providerId,
        Authorization: `Bearer ${token}`,
      },
    },
  );
};

//
// Get the transaction ID for the given enquiry ID
//
export const getTransactionForEnquiry = (token: string, enquiryId: string): Promise<AxiosResponse<IdWrapper>> => {
  const providerId: string = getProviderId(token);

  return axios.get(
    `/sg/v1/transactions/enquiry/${enquiryId}`,
    {
      headers: {
        'X-ProviderId': providerId, // only for local testing, real users are extracted from token
        Authorization: `Bearer ${token}`,
      },
    },
  )
    .catch((error: any) => Promise.reject(new Error(`Error retrieving transaction ID for enquiry ID: ${error}`)));
};

//
// Get the transaction details for the given enquiry ID
//
export const getTransactionsForEnquiry = (token: string, enquiryId: string): Promise<AxiosResponse<TransactionState[]>> => {
  const providerId: string = getProviderId(token);

  return axios.get(
    `/sg/v3/transactions/enquiry/${enquiryId}`,
    {
      headers: {
        'X-ProviderId': providerId, // only for local testing, real users are extracted from token
        Authorization: `Bearer ${token}`,
      },
    },
  )
    .then((response: AxiosResponse<TransactionState[]>) => {
      response.data.forEach((medicalExam: TransactionState) => {
        medicalExam.bookingTime = medicalExam.bookingTime ? new Date(medicalExam.bookingTime) : null;
        medicalExam.created = new Date(medicalExam.created);
        medicalExam.updated = new Date(medicalExam.updated);
      });
      return response;
    })
    .catch((error: any) => Promise.reject(new Error(`Error retrieving transaction details for enquiry ID: ${error}`)));
};

//
// Get manual review enquiries
//
export const getManualOverrides = (token: string, requestParams: GetEnquiryParams): Promise<AxiosResponse<ManualEnquiries>> => {
  const url = '/sg/v2/overrides/manual';
  const providerId: string = getProviderId(token);

  return axios.get<ManualEnquiries>(url, {
    headers: {
      'X-ProviderId': providerId, // only for local testing, real users are extracted from token
      Authorization: `Bearer ${token}`,
    },
    params: {
      limit: requestParams.pageSize,
      offset: requestParams.pageSize * (requestParams.page - 1),
      sortBy: requestParams.sortOrder ? requestParams.sortField : null,
      sortDirection: requestParams.sortOrder,
      filterField: requestParams.filterField || null,
      filterValue: requestParams.filterValue || null,
      excludeFilter: requestParams.excludeFilter || null,
    },
    transformResponse: (rawData: string) => {
      const data = JSON.parse(rawData);
      return { ...data, data: data.data.map(format) };
    },
  })
    .then((res: AxiosResponse<ManualEnquiries>) => {
      res.data.data.forEach((data) => {
        data.firstRequestedDate = new Date(data.firstRequestedDate);
        data.enquiryId = data.enquiryId || '';
        data.applicationId = data.applicationId || '';
        data.firstName = data.firstName || '';
        data.middleName = data.middleName || '';
        data.surname = data.surname || '';
        data.evidenceProviders.forEach((provider) => {
          provider.bookingStatusUpdated = new Date(provider.bookingStatusUpdated);
          provider.evidenceRequested = provider.evidenceRequested || '';
          provider.evidenceProvider = provider.evidenceProvider || '';
          provider.bookingStatus = provider.bookingStatus || '';
        });
        data.actioned = false;
      });
      return res;
    });
};

//
// Retry an enquiry
//
export const retryActionEnquiry = (token: string, enquiryId: string, action: string, notification: string): Promise<AxiosResponse<void>> => {
  const url = '/oc/v1/retry';
  const providerId: string = getProviderId(token);
  const command: RetryAggregateCommand = {
    aggregateId: enquiryId,
    action: action,
    notification: notification,
  };

  return axios.post(
    url,
    command,
    {
      headers: {
        'X-ProviderId': providerId,
        Authorization: `Bearer ${token}`,
      },
    },
  );
}

//
// Get an enquiry summary
//
export const getSummaryEnquiry = (token: string, enquiryId: string): Promise<AxiosResponse<EnquirySummary>> => {
  const providerId: string = getProviderId(token);

  return axios.get<EnquirySummary>(
    `/sg/v1/summary/${enquiryId}`,
    {
      headers: {
        'X-ProviderId': providerId,
        Authorization: `Bearer ${token}`,
      },
      params: {
        verbose: true,
        detailedEvents: true,
        format: 'json',
      },
    },
  )
    .then((response: AxiosResponse<EnquirySummary>) => {
      response.data.dob = new Date(response.data.dob);
      response.data.createdTimeStamp = new Date(response.data.createdTimeStamp);
      if (response.data.events) {
        response.data.events.forEach(event => {
          event.eventTransactionTS = new Date(event.eventTransactionTS);
        });
      }
      if (response.data.transactions) {
        response.data.transactions.forEach(transaction => {
          transaction.statusUpdated = new Date(transaction.statusUpdated);
          if (transaction.bookingDateTime) transaction.bookingDateTime = new Date(transaction.bookingDateTime);
          if (transaction.events) {
            transaction.events.forEach(event => {
              event.eventTransactionTS = new Date(event.eventTransactionTS);
            })
          }
        })
      }
      if (response.data.chainedEnquiry) {
        response.data.chainedEnquiry.createdTimeStamp = new Date(response.data.chainedEnquiry.createdTimeStamp);
        if (response.data.chainedEnquiry.events) {
          response.data.chainedEnquiry.events.forEach(event => {
            event.eventTransactionTS = new Date(event.eventTransactionTS);
          })
        }
      }
      return response;
    });
};

//
// Get provider security mappings
//
export const getProviderSecurityMapping = (token: string): Promise<AxiosResponse<ProviderMappingResponse>> => {
  const providerId: string = getProviderId(token);

  return axios.get<ProviderMappingResponse>(
    '/sg/v1/provider/mapping',
    {
      headers: {
        'X-ProviderId': providerId,
        Authorization: `Bearer ${token}`,
      },
      params: {
        format: 'json',
      },
    },
  )

};